import { borderColor, createBreakpoints } from '@mui/system';
import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import { BrandColors } from '../../constants/brandColors';
import { createTheme, Theme } from '@mui/material/styles';
import { isArabicLocale } from '../../common/utils/checkLocalePlayer';
import {
  TemplateType,
  Template,
  Alert,
  PasswordStrengthMeter,
  BasicTaskListIcons,
  SvgStyles,
  DialogBox,
  IconHover,
} from '../../store/interfaces';
import { defaultBranding } from '../../constants';
import { isJourneyTheme } from '../../common/utils/fallbackCondition';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    default?: Palette['primary'];
    borderColor?: Palette['primary'];
    headerColor?: Palette['primary'];
    taskTileColor?: Palette['primary'];
    taskPageColor?: Palette['primary'];
    alert?: Alert;
    passwordStrengthMeter?: PasswordStrengthMeter;
    basicTaskListIcons?: BasicTaskListIcons;
    svgStyles?: SvgStyles;
    linkTextColor?: string;
    dialogBox?: DialogBox;
    iconHover?: IconHover;
  }
  interface PaletteOptions {
    default?: PaletteOptions['primary'];
    borderColor?: PaletteOptions['primary'];
    headerColor?: PaletteOptions['primary'];
    taskTileColor?: PaletteOptions['primary'];
    taskPageColor?: PaletteOptions['primary'];
    alert?: Alert;
    passwordStrengthMeter?: PasswordStrengthMeter;
    basicTaskListIcons?: BasicTaskListIcons;
    svgStyles?: SvgStyles;
    linkTextColor?: string;
    dialogBox?: DialogBox;
    iconHover?: IconHover;
  }
}

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 640, // mobile = 0 to 639
    md: 768, // tab = 640 to 1023
    lg: 1024, // desktop = 1024 and above
    xl: 1366,
  },
});

const h1 = {
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: 24,
  lineHeight: '32px',
  [breakpoints.up('lg')]: {
    fontSize: 32,
    lineHeight: '44px',
  },
};

const h2 = {
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: 20,
  lineHeight: '28px',
  [breakpoints.up('lg')]: {
    fontSize: 24,
    lineHeight: '32px',
  },
};

const h3 = {
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: 16,
  lineHeight: '22px',
  [breakpoints.up('lg')]: {
    fontSize: 18,
    lineHeight: '24px',
  },
};

export const baseTheme = (template: Template): Theme => {
  const {
    layoutCode,
    type,
    settings: {
      utilityBar,
      page: {
        backgroundColor,
        containerColor,
        buttonStyle1,
        buttonStyle2,
        fontColor,
        fontType,
        backgroundImage,
        primaryColor,
        secondaryColor,
        terniaryColor,
        quaternaryColor,
        taskListTitleColor,
        taskListSectionColor,
      },
      otherElements = defaultBranding.otherElements,
    },
  } = template;

  // If backgound image is not added then the body should not contain it's css
  let backgroundImagePath = {};
  if (backgroundImage) {
    if (isJourneyTheme(layoutCode)) {
      backgroundImagePath = {
        backgroundImage: `url(${appendCdnToImagePath(backgroundImage.path)})`,
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        backgroundPosition: '50% 40px',
        backgroundRepeat: 'no-repeat',
        [breakpoints.up('lg')]: {
          backgroundPosition: '50% 50px',
        },
      };
    } else {
      backgroundImagePath = {
        [breakpoints.up('lg')]: {
          backgroundImage: `url(${appendCdnToImagePath(backgroundImage.path)})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: '50% 50px',
          backgroundRepeat: 'no-repeat',
        },
      };
    }
  }

  return createTheme({
    palette: {
      primary: {
        main: buttonStyle1.fillColor,
        contrastText: buttonStyle1.textColor,
      },
      secondary: {
        main: buttonStyle2.fillColor,
        contrastText: buttonStyle2.textColor,
      },
      error: {
        main: otherElements?.error?.color || BrandColors.red39,
      },
      success: {
        main: otherElements?.success?.color || BrandColors.passwordValidationSuccess,
      },
      background: {
        default: backgroundColor,
        paper: containerColor,
      },
      info: {
        main: BrandColors.grey95,
      },
      text: {
        primary: fontColor,
      },
      action: {
        disabled: BrandColors.grey40,
        disabledBackground: otherElements?.MuiButton?.disabledBackgroundColor,
      },
      taskTileColor: {
        main: terniaryColor || primaryColor || BrandColors.green57,
        dark: quaternaryColor || secondaryColor || BrandColors.link,
      },
      taskPageColor: {
        main: taskListTitleColor || primaryColor || BrandColors.green57,
        dark: taskListSectionColor || primaryColor || BrandColors.link,
      },

      alert: {
        outlinedInfo: {
          background: otherElements.alert?.outlinedInfo?.background,
          border: otherElements.alert?.outlinedInfo?.border,
        },
        outlinedInfoTransparent: {
          border: otherElements.alert?.outlinedInfo?.border,
          background: containerColor,
        },
        outlinedError: {
          background: otherElements.alert?.outlinedError.background,
          border: `1px solid ${otherElements?.error?.color}`,
        },
        outlinedSuccess: {
          background: otherElements.alert?.outlinedSuccess.background,
          border: `1px solid ${buttonStyle1.fillColor}`,
        },
      },
      passwordStrengthMeter: {
        weak: { color: otherElements.passwordStrengthMeter?.weak.color },
        medium: { color: otherElements.passwordStrengthMeter?.medium.color },
        strong: { color: otherElements.passwordStrengthMeter?.strong.color },
        border: otherElements.passwordStrengthMeter?.border,
      },
      basicTaskListIcons: {
        lockAndCompleted: { color: otherElements.basicTaskListIcons?.lockAndCompleted.color },
      },
      svgStyles: {
        stroke: otherElements?.svgStyles.stroke,
        fill: otherElements?.svgStyles.fill,
        fillForElements: otherElements?.svgStyles.fillForElements,
      },
      linkTextColor: otherElements.MuiLink.color,
      dialogBox: { ...otherElements?.dialogBox },
      borderColor: {
        main: otherElements?.borderColor,
      },
      iconHover: {
        background: otherElements?.iconHover?.background,
        color: otherElements?.iconHover?.color,
      },
    },
    breakpoints,
    direction: isArabicLocale() ? 'rtl' : 'ltr',
    typography: {
      fontFamily: `'${fontType}', sans-serif`,
      h1,
      h2,
      h3,
      body1: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: 16,
        lineHeight: '24px',
        color: fontColor,
      },
      body2: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: 16,
        lineHeight: '24px',
        color: fontColor,
      },
      subtitle1: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: 14,
        lineHeight: '22px',
        color: fontColor,
      },
      caption: {
        color: `${fontColor} !important`,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            '*:not(input):focus-visible': {
              outline: 'none !important',
              boxShadow: `0 0 0 2px ${BrandColors.white}, 0 0 0 5px ${BrandColors.focusOutline} !important`,
            },
            '* .Mui-focusVisible': {
              outline: 'none !important',
              boxShadow: `0 0 0 2px ${BrandColors.white}, 0 0 0 5px ${BrandColors.focusOutline} !important`,
            },
            '* .MuiRadio-root.Mui-focusVisible, * .MuiCheckbox-root.Mui-focusVisible': {
              borderRadius: '50%',
            },
            body: { ...backgroundImagePath },
            /**
             * For IE background image jitter bug.
             * Previously, there was overflow hidden in html.
             * But that broke material ui select all over the app (CAW-1078).
             * Now, we only apply overflow hidden when general content corrin is mounted.
             * This bugfix for ie was for corrin layout, that's why the overflow logic is moved there.
             */
            '@media screen and (-ms-high-contrast: none)': {
              html: {
                // overflow: 'hidden',
                height: '100%',
              },
              body: {
                overflow: 'auto',
                height: '100%',
              },
            },
            '@supports not (-ms-high-contrast: none)': {
              body: { ...backgroundImagePath },
            },
            /**
             * Custom Scrollbar
             */
            // Mozilla Firefox
            '*': {
              scrollbarWidth: 'thin',
              scrollbarColor: BrandColors.blackWithOpacity25,
              scrollBehavior: 'smooth',
            },
            // Chrome & Safari
            '*::-webkit-scrollbar': {
              width: '0.5rem',
            },
            '*::-webkit-scrollbar-track': {
              borderRadius: '0.5rem',
              backgroundColor: BrandColors.grey95,
            },
            '*::-webkit-scrollbar-thumb': {
              borderRadius: '0.5rem',
              backgroundColor: BrandColors.blackWithOpacity25,
            },
            // IE end
            p: {
              fontStyle: 'normal',
              fontWeight: 'normal',
              fontSize: 16,
              lineHeight: '24px',
              [breakpoints.up('lg')]: {
                fontSize: 20,
                lineHeight: '32px',
              },
            },
            a: {
              color: otherElements.MuiLink.color || BrandColors.muiLink,
            },
            figure: {
              margin: '0px',
            },
            figcaption: {
              textAlign: 'center',
            },
            h1,
            h2,
            h3,
            // Cookiebot styles
            '#CybotCookiebotDialog p': {
              fontSize: '9.5pt',
              marginTop: 0,
              lineHeight: 1.231,
            },
            '#CybotCookiebotDialogDetailBodyContentTextAbout p': {
              fontSize: '9pt',
            },

            // Accessible Icons srOnly Text
            '.MuiTypography-srOnly.srIcon': {
              opacity: 0,
              width: '100%',
              height: '100%',
              fontSize: '40px',
            },

            '._hj-widget-container [class$=__styles__modalOverlay]': {
              background: '#777777 !important',
            },
            /**
             * STOP OR START animation as per system configurations of animation effects
             */
            '@media (prefers-reduced-motion: reduce)': {
              '*': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          regular: {
            minHeight: '40px',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: utilityBar.backgroundColor,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '5px',
            lineHeight: '26px',
            textTransform: 'none',
            padding: '7px 16px',
            fontSize: type === TemplateType.BASIC_EXPERIENCE_LITE ? 12 : 18,
            fontWeight: 600,
          },
          outlinedPrimary: {
            border: '2px solid',
            color: buttonStyle1.textColor,
            backgroundColor: buttonStyle1.fillColor,
            borderColor: buttonStyle1.outlineColor,
            '&:hover, &:active': {
              /** check for solid button and inverted case */
              border: '2px solid',
              color: buttonStyle1?.hoverTextColor || buttonStyle1.fillColor,
              borderColor: buttonStyle1.hoverOutlineColor
                ? buttonStyle1.hoverOutlineColor === buttonStyle1.hoverFillColor
                  ? buttonStyle1.hoverTextColor
                  : buttonStyle1.hoverOutlineColor
                : buttonStyle1.outlineColor === buttonStyle1.fillColor
                  ? buttonStyle1.textColor
                  : buttonStyle1.outlineColor,
              backgroundColor: buttonStyle1?.hoverFillColor || buttonStyle1.textColor,
            },
            '&:disabled': {
              /** check for solid button */
              border: '2px solid',
              color: otherElements?.MuiButton?.disabledTextColor,
              backgroundColor: otherElements?.MuiButton?.disabledBackgroundColor,
              borderColor: otherElements?.MuiButton?.disabledBackgroundColor,
            },
          },
          outlinedSecondary: {
            border: '2px solid',
            color: buttonStyle2.textColor,
            backgroundColor: buttonStyle2.fillColor,
            borderColor: buttonStyle2.outlineColor,
            '&:hover, &:active': {
              border: '2px solid',
              borderColor: buttonStyle2.hoverOutlineColor
                ? buttonStyle2.hoverOutlineColor === buttonStyle2.hoverFillColor
                  ? buttonStyle2.hoverTextColor
                  : buttonStyle2.hoverOutlineColor
                : buttonStyle2.outlineColor === buttonStyle2.fillColor
                  ? buttonStyle2.textColor
                  : buttonStyle2.outlineColor,
              color: buttonStyle2?.hoverTextColor || buttonStyle2.fillColor,
              backgroundColor: `${buttonStyle2?.hoverFillColor || buttonStyle2.textColor} !important`,
            },
            '&:disabled': {
              border: '2px solid',
              color: otherElements?.MuiButton?.disabledTextColor,
              backgroundColor: otherElements?.MuiButton?.disabledBackgroundColor,
              borderColor: otherElements?.MuiButton?.disabledBackgroundColor,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: 18,
            fontWeight: 'bold',
            lineHeight: '24px',
            letterSpacing: 1,
            padding: '0 0.75rem',
            minHeight: '2.5rem',
            minWidth: '6.625rem !important',
            '&:hover, &:focus, &$selected': {
              color: buttonStyle1.fillColor,
            },
          },
          textColorPrimary: {
            color: fontColor,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '2.5rem',
            overflow: 'visible',
            '& .MuiTabs-scroller': {
              overflow: 'visible !important',
            },
          },
          indicator: {
            height: '0.25rem',
            minWidth: '6.625rem !important',
            width: '6.625rem !important',
            backgroundColor: buttonStyle1.fillColor,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          elevation1: {
            boxShadow: `2px 2px 4px ${BrandColors.lightGrey40}`,
            borderRadius: '0.3125rem',
            border: otherElements.border,
          },
          elevation8: {
            border: otherElements.border,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: otherElements?.MuiInputBase?.backgroundColor || BrandColors.white,
            color: fontColor,
            boxSizing: 'border-box',
            '& .Mui-disabled': {
              backgroundColor: otherElements?.MuiButton?.disabledBackgroundColor,
              color: otherElements?.MuiButton?.disabledTextColor,
            },
            '[data-whatinput="keyboard"][data-whatintent="keyboard"]  &:focus-within': {
              outline: 'none !important',
              boxShadow: `0 0 0 2px ${BrandColors.white}, 0 0 0 5px ${BrandColors.focusOutline} !important`,
            },
            '[data-whatinput="keyboard"][data-whatintent="keyboard"] &:focus-within': {
              outline: 'none !important',
              boxShadow: `0 0 0 2px ${BrandColors.white}, 0 0 0 5px ${BrandColors.focusOutline} !important`,
            },
          },
          input: {
            //border: otherElements.border,
            // Webkit browsers autofill css
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
              '-webkit-text-fill-color': fontColor,
              '-webkit-box-shadow': `0 0 0px 1000px ${otherElements?.MuiInputBase?.backgroundColor} inset`,
            },
            '&::-webkit-input-placeholder': {
              color: otherElements?.PlaceHolder?.color || BrandColors.grey33,
              opacity: 0.85,
            },
            '&:-ms-input-placeholder': {
              color: otherElements?.PlaceHolder?.color || BrandColors.grey33,
              opacity: 0.85,
            },
            '&::-ms-input-placeholder': {
              color: otherElements?.PlaceHolder?.color || BrandColors.grey33,
              opacity: 0.85,
            },
            '&::placeholder': {
              color: otherElements?.PlaceHolder?.color || BrandColors.grey33,
              opacity: 0.85,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: fontColor,
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: `${BrandColors.grey88} !important`,
            },
            whiteSpace: 'normal',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          input: {
            boxSizing: 'inherit',
            height: '2.5rem',
            padding: '0.625rem 1rem',
            borderRadius: '2px',
            border: `1px solid ${otherElements.borderColorInputField}`,
            '&:not(.Mui-disabled):hover': {
              border: '1px solid',
              borderColor: buttonStyle1.fillColor,
            },
            '&:not(.Mui-disabled):focus-within': {
              border: '2px solid',
              borderColor: buttonStyle1.fillColor,
            },
          },
          root: {
            borderRadius: '2px',
            '& .MuiInputBase-input': {
              height: '2.5rem',
            },
            '&.Mui-error .MuiInput-input': {
              borderColor: otherElements.error.color,
              '&:hover': {
                borderColor: otherElements.error.color,
              },
              '&:focus-within': {
                borderColor: otherElements.error.color,
              },
            },
            '& .MuiSelect-root.MuiSelect-select': {
              paddingRight: '2rem',
            },
            '&.MuiInputBase-adornedEnd': {
              border: `1px solid ${otherElements.borderColorInputField}`,
              height: '2.5rem',
              '&:not(.Mui-disabled):hover': {
                border: `1px solid ${buttonStyle1.fillColor}`,
              },
              '&:not(.Mui-disabled):focus-within': {
                border: `2px solid ${buttonStyle1.fillColor}`,
              },
            },
            '&.MuiInputBase-adornedEnd:not(.Mui-disabled) .MuiInput-input': {
              border: 'none',
            },
            '&.MuiInputBase-adornedEnd .MuiInputAdornment-positionEnd': {
              marginRight: '0.5rem',
            },
            '&.MuiInputBase-root .MuiSelect-icon': {
              right: '0.625rem',
            },
          },
          underline: {
            '&:after, &:before': {
              content: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            border: '1px solid',
            top: '-.35rem',
            '&.MuiOutlinedInput-notchedOutline': {
              borderColor: `${otherElements?.PlaceHolder?.color}`,
            },
            '&:hover, &:active': {
              border: '2px solid',
              borderColor: buttonStyle1.fillColor,
            },
          },
          root: {
            borderRadius: '2px',
            '&:hover': {
              borderColor: buttonStyle1.fillColor,
            },
          },
          input: {
            padding: '8px 16px',
            '&.Mui-disabled': {
              '-webkit-text-fill-color': `${otherElements?.MuiButton?.disabledTextColor} !important`,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: 16,
            color: `${otherElements.error.color} !important`,
            lineHeight: '24px',
            alignItems: 'center',
            fontFamily: `'${fontType}', sans-serif`,
          },
          contained: {
            marginLeft: 0,
            marginRight: 0,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            fontFamily: `'${fontType}', sans-serif`,
            fontStyle: 'normal',
            fontWeight: 'normal',
            fontSize: 16,
            textDecoration: 'underline',
            textDecorationColor: 'inherit',
            lineHeight: '24px',
            color: otherElements?.MuiLink?.color || BrandColors.muiLink,
            '& span[role=link]:focus': {
              outline: '1px dotted black',
            },
          },
          underlineHover: {
            textDecoration: 'underline',
            '&:hover, :active': {
              textDecoration: 'none',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          barColorPrimary: {
            backgroundColor: buttonStyle1.fillColor,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: otherElements.divider?.backgroundColor,
          },
          light: {
            backgroundColor: otherElements.divider?.backgroundColor,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            height: '1.375rem',
            width: '2.375rem',
            padding: 0,
            marginRight: '0.25rem',
            borderRadius: '50px',
            '&:hover': {
              boxShadow: `0px 0px 2px 1px ${buttonStyle1.fillColor}`,
            },
            '&:focus-within .Mui-focusVisible': {
              boxShadow: 'none !important',
            },
            '&:focus-within:has(.Mui-focusVisible)': {
              outline: 'none !important',
              boxShadow: `0 0 0 2px ${BrandColors.white}, 0 0 0 5px ${BrandColors.focusOutline} !important`,
            },
          },
          switchBase: {
            padding: 1,
            '&.Mui-checked': {
              transform: 'translateX(1rem)',
              boxShadow: `inset 2px 2px 3px ${BrandColors.blackWithOpacity25}`,
              '& + .MuiSwitch-track': {
                opacity: 1,
                border: 'none',
              },
            },
          },
          thumb: {
            width: '1.25rem',
            height: '1.25rem',
            boxSizing: 'border-box',
            color: BrandColors.grey98,
            border: `1px solid ${BrandColors.grey77}`,
            boxShadow: `2px 0px 2px ${BrandColors.blackWithOpacity25}`,
          },
          track: {
            borderRadius: '50px',
            border: `1px solid ${BrandColors.grey40}`,
            backgroundColor: BrandColors.grey40,
            opacity: 1,
            boxSizing: 'border-box',
            boxShadow: `inset -2px 2px 3px ${BrandColors.blackWithOpacity25}`,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: otherElements.MuiSvgIcon?.iconColor,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            textWrap: 'wrap',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: otherElements.MuiSvgIcon?.iconColor,
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: otherElements.MuiInputBase.backgroundColor,
            border: `1px solid ${BrandColors.grey40}`,
            boxSizing: 'border-box',
            boxShadow: `2px 2px 5px ${BrandColors.lightGrey40}`,
            borderRadius: '2px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: otherElements.MuiSvgIcon?.iconColor,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: otherElements.MuiSvgIcon?.iconColor,
            top: 'auto',
          },
          select: {
            '&:focus': {
              backgroundColor: otherElements.MuiInputBase.backgroundColor,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            alignItems: 'start',
            '& .MuiCheckbox-root, & .MuiRadio-root': {
              marginRight: '4px',
              padding: '2px 5px',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: fontColor,
            height: 24,
            lineHeight: '24px',
            display: 'contents !important',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            'z-index': '1',
          },
        },
      },
    },
  });
};
